import { api } from "@/lib/api";

export type SeedPropertySchema = {
  type?: string;
  description?: string;
  ui?: { widget?: string; placeholder?: string; group?: string };
};

export type SeedPageSchema = {
  slug: string;
  title: string;
  type: string;
  properties: Record<string, SeedPropertySchema>;
};

export type SeedSchema = {
  pages: SeedPageSchema[];
};

export type KbWiki = {
  id: number;
  account_id: number;
  brand_id: number | null;
  kind: string;
  title: string;
  seed_schema: SeedSchema;
  schema_doc: string | null;
  prompt_pack: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type KbPage = {
  id: number;
  account_id: number;
  wiki_id: number;
  slug: string;
  title: string;
  type: string;
  body: string | null;
  structured: Record<string, unknown>;
  is_seed: boolean;
  frontmatter: Record<string, unknown>;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
};

export type KbRevision = {
  id: number;
  page_id: number;
  account_id: number;
  actor_type: string;
  actor_id: number;
  source: string;
  source_ref: string | null;
  snapshot: Record<string, unknown>;
  created_at: string;
  page_title?: string;
  page_slug?: string;
};

export type KbRawSource = {
  id: number;
  account_id: number;
  wiki_id: number | null;
  title: string | null;
  body: string | null;
  content_type: string | null;
  status: string;
  ingest_error: string | null;
  has_file: boolean;
  filename?: string | null;
  created_at: string;
  updated_at: string;
};

/** Wiki 工作台进度条：服务端 Kb::ProgressStats 汇总。 */
export type KbWikiProgress = {
  seed_total: number;
  seed_completed: number;
  seed_pending: number;
  entry_total: number;
  schema_fields_filled: number;
  schema_fields_total: number;
  richness_score: number;
  inbox_pending: number;
  last_updated_at: string | null;
};

export type AutofillTraceStep =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string }
  | { role: "tool_call"; id?: string; name: string; arguments?: Record<string, unknown> }
  | { role: "tool_result"; tool_call_id?: string; content: string };

export type AutofillStreamEvent =
  | { id: number; type: "trace_step"; step: AutofillTraceStep }
  | { id: number; type: "still_working"; message: string }
  | {
      id: number;
      type: "done";
      proposed_value: string;
      trace?: AutofillTraceStep[];
      meta: Record<string, unknown>;
    }
  | { id: number; type: "error"; code: string; message: string };

export type AutofillResult = {
  proposed_value: string;
  trace?: AutofillTraceStep[];
  meta: Record<string, unknown>;
};

export function createKbApi(accountId: string) {
  const prefix = "/kb";

  return {
    listWikis() {
      return api.fetchForAccount<{ wikis: KbWiki[] }>(accountId, `${prefix}/wikis`);
    },

    getWiki(wikiId: number) {
      return api.fetchForAccount<{ wiki: KbWiki }>(accountId, `${prefix}/wikis/${wikiId}`);
    },

    getWikiProgress(wikiId: number) {
      return api.fetchForAccount<{ progress: KbWikiProgress }>(
        accountId,
        `${prefix}/wikis/${wikiId}/progress`,
      );
    },

    listPages(wikiId: number) {
      return api.fetchForAccount<{ pages: KbPage[] }>(accountId, `${prefix}/pages?wiki_id=${wikiId}`);
    },

    getPage(pageId: number) {
      return api.fetchForAccount<{ page: KbPage }>(accountId, `${prefix}/pages/${pageId}`);
    },

    createPage(payload: {
      wiki_id: number;
      title: string;
      /** 省略时由服务端生成随机唯一 slug */
      slug?: string;
      type?: string;
      body?: string;
    }) {
      return api.fetchForAccount<{ page: KbPage }>(accountId, `${prefix}/pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    updatePage(
      pageId: number,
      payload: Partial<{
        slug: string;
        title: string;
        type: string;
        body: string;
        structured: Record<string, unknown>;
        frontmatter: Record<string, unknown>;
      }>,
    ) {
      return api.fetchForAccount<{ page: KbPage }>(accountId, `${prefix}/pages/${pageId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    deletePage(pageId: number) {
      return api.fetchForAccount<{ page: KbPage }>(accountId, `${prefix}/pages/${pageId}`, {
        method: "DELETE",
      });
    },

    listPageRevisions(pageId: number) {
      return api.fetchForAccount<{ revisions: KbRevision[] }>(
        accountId,
        `${prefix}/pages/${pageId}/revisions`,
      );
    },

    listRevisions(params?: { wiki_id?: number }) {
      const query = params?.wiki_id ? `?wiki_id=${params.wiki_id}` : "";
      return api.fetchForAccount<{ revisions: KbRevision[] }>(accountId, `${prefix}/revisions${query}`);
    },

    listRawSources(wikiId?: number) {
      const query = wikiId ? `?wiki_id=${wikiId}` : "";
      return api.fetchForAccount<{ raw_sources: KbRawSource[] }>(
        accountId,
        `${prefix}/raw_sources${query}`,
      );
    },

    createTextRawSource(payload: { title?: string; body: string; wiki_id?: number }) {
      return api.fetchForAccount<{ raw_source: KbRawSource }>(accountId, `${prefix}/raw_sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },

    startAutofill(payload: {
      task_id: string;
      target: { page_id: number; field: string };
      current_value?: string;
      hint?: string;
    }) {
      return api.fetchForAccount<{ run_id: string; status: string }>(
        accountId,
        `${prefix}/autofill`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },

    pollAutofillStream(runId: string, afterId?: number) {
      const query = afterId ? `?after_id=${afterId}` : "";
      return api.fetchForAccount<{ events: AutofillStreamEvent[]; status: string }>(
        accountId,
        `${prefix}/autofill/${runId}/stream${query}`,
      );
    },

    /** @deprecated Use startAutofill + waitForAutofill instead */
    autofill(payload: {
      task_id: string;
      target: { page_id: number; field: string };
      current_value?: string;
      hint?: string;
    }) {
      return api.fetchForAccount<AutofillResult>(
        accountId,
        `${prefix}/autofill`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
    },
  };
}

export function findSeedPageDef(wiki: KbWiki, page: KbPage): SeedPageSchema | undefined {
  return wiki.seed_schema.pages.find((def) => def.slug === page.slug);
}

const AUTOFILL_POLL_INTERVAL_MS = 800;
const AUTOFILL_POLL_TIMEOUT_MS = 300_000;
const AUTOFILL_POLL_MAX_ATTEMPTS = Math.ceil(AUTOFILL_POLL_TIMEOUT_MS / AUTOFILL_POLL_INTERVAL_MS);

function normalizeTraceStep(step: AutofillTraceStep): AutofillTraceStep {
  return step;
}

export async function waitForAutofill(
  accountId: string,
  runId: string,
  onProgress?: (trace: AutofillTraceStep[]) => void,
  onHeartbeat?: (message: string) => void,
): Promise<AutofillResult> {
  const kb = createKbApi(accountId);
  const trace: AutofillTraceStep[] = [];
  let lastEventId = 0;

  for (let attempt = 0; attempt < AUTOFILL_POLL_MAX_ATTEMPTS; attempt += 1) {
    const { events } = await kb.pollAutofillStream(runId, lastEventId || undefined);

    for (const event of events) {
      lastEventId = Math.max(lastEventId, event.id);

      if (event.type === "trace_step") {
        trace.push(normalizeTraceStep(event.step));
        onProgress?.([...trace]);
      }

      if (event.type === "still_working") {
        onHeartbeat?.(event.message);
      }

      if (event.type === "done") {
        return {
          proposed_value: event.proposed_value,
          trace: Array.isArray(event.trace) && event.trace.length > 0 ? event.trace : trace,
          meta: event.meta,
        };
      }

      if (event.type === "error") {
        throw new Error(event.message);
      }
    }

    await new Promise((resolve) => window.setTimeout(resolve, AUTOFILL_POLL_INTERVAL_MS));
  }

  throw new Error("AI 填充超时，请重试");
}
